import { GridColDef } from '@mui/x-data-grid-pro';
import { useMemo, useState } from 'react';
import {
  useQuery,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { UserKeys, getAppLogs } from '../../../../api/users-api';
import { AppLog } from '../../../../../types';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import {
  DateRange,
  PickersShortcutsItem,
  SingleInputDateRangeField,
} from '@mui/x-date-pickers-pro';
import dayjs, { Dayjs } from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { useParams } from 'react-router-dom';
import { createGridOverlays } from '../../../grid-overlay/no-data-overlay-helpers';
import SharedDataGridServer from '../../../../shared/shared-data-grid-server';
import SharedToolbar from '../../../../shared/shared-toolbar-latest';
import SharedIconButton from '../../../../shared/buttons/shared-icon-button';

import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import History from '@mui/icons-material/History';

const columns: GridColDef<AppLog>[] = [
  {
    field: 'createdDate',
    headerName: 'Date',
    editable: false,
    sortable: false,
    filterable: false,
    width: 200,
    type: 'dateTime',
    valueGetter: (_value, row) => {
      if (row.createdDate === 0) return '';
      return new Date(row.createdDate);
    },
    valueFormatter: (value) => {
      if (value === '') return '';
      return new Date(value as string).toLocaleString();
    },
  },
  {
    field: 'performingUser',
    headerName: 'Performing User',
    editable: false,
    sortable: false,
    filterable: false,
    width: 300,
  },
  {
    field: 'action',
    headerName: 'Action',
    editable: false,
    sortable: false,
    filterable: false,
    flex: 1,
  },
];

const shortcutsItems: PickersShortcutsItem<DateRange<Dayjs>>[] = [
  {
    label: 'Last Week',
    getValue: () => {
      const today = dayjs();
      const prevWeek = today.subtract(7, 'day');
      return [prevWeek.startOf('week'), prevWeek.endOf('week')];
    },
  },
  {
    label: 'Last 7 Days',
    getValue: () => {
      const today = dayjs();
      return [today.subtract(7, 'day'), today];
    },
  },
  {
    label: 'Last Month',
    getValue: () => {
      const today = dayjs();
      const prevMonth = today.subtract(1, 'month');
      return [prevMonth.startOf('month'), prevMonth.endOf('month')];
    },
  },
  { label: 'Clear', getValue: () => [null, null] },
];

export default function LogsPage() {
  const { administrationRoles } = useUserSettings();

  const { groupEmail } = useParams() as { groupEmail: string };

  const queryClient = useQueryClient();

  const today = dayjs();
  const pageSize = 200;
  const [paginationModel, setPaginationModel] = useState<{
    pageSize: number;
    page: number;
    cursor: string | null;
    dateRange: DateRange<Dayjs>;
  }>({
    pageSize: pageSize,
    page: 0,
    cursor: '',
    dateRange: [today.subtract(7, 'day'), today],
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      UserKeys.GroupAppLogs,
      groupEmail,
      paginationModel.page,
      paginationModel.dateRange[0]?.toDate().getTime() || 0,
      paginationModel.dateRange[1]?.toDate().getTime() || 0,
    ],
    queryFn: () =>
      getAppLogs(
        groupEmail,
        paginationModel.cursor || '',
        paginationModel.dateRange[0]?.toDate().getTime() || 0,
        paginationModel.dateRange[1]?.toDate().getTime() || 0
      ),
    staleTime: 1000 * 60 * 3,
    placeholderData: keepPreviousData,
    enabled: isAbleToSeeUIElement(
      administrationRoles,
      AdministrationRoles.ViewAppLogs.value
    ),
    meta: {
      errorMessage:
        'There was an issue getting app logs for this group. Please refresh and try again.',
    },
  });

  function CustomToolbar() {
    return (
      <SharedToolbar
        noColumn
        noFilter
        noQuickFilter
        leftPanel={
          <Stack flexDirection="row" gap={1} alignItems="center">
            {!isAbleToSeeUIElement(
              administrationRoles,
              AdministrationRoles.ViewAppLogs.value
            ) ? (
              <Alert severity="error">
                The "View App Logs" permission is required to view App Logs.
              </Alert>
            ) : (
              <>
                <DateRangePicker
                  sx={{ width: 250 }}
                  label={'Date Range'}
                  value={paginationModel.dateRange}
                  calendars={2}
                  onChange={(newValue) => {
                    setPaginationModel({
                      pageSize: pageSize,
                      page: 0,
                      cursor: '',
                      dateRange: newValue,
                    });
                  }}
                  slotProps={{
                    shortcuts: {
                      items: shortcutsItems,
                    },
                    actionBar: { actions: [] },
                  }}
                  slots={{ field: SingleInputDateRangeField }}
                />
                <Alert severity="info">
                  App logs are filtered to show actions that occured on this
                  group.
                </Alert>
              </>
            )}
          </Stack>
        }
        secondaryButtons={
          <SharedIconButton
            variant="refresh"
            onClick={() => {
              setPaginationModel({
                ...paginationModel,
                pageSize: pageSize,
                page: 0,
                cursor: '',
              });
              void queryClient.invalidateQueries({
                queryKey: [UserKeys.GroupAppLogs, groupEmail],
              });
            }}
            tooltipLabel="Refresh"
          />
        }
      />
    );
  }

  const parsedData = useMemo(
    () =>
      data?.results.map((result) => {
        return {
          ...result,
          action: result.action
            ? result.action.replace('&#39;', "'").replace('&quot;', '"')
            : '',
          permission: result.permission
            ? result.permission.replace('&#39;', "'").replace('&quot;', '"')
            : '',
          id: uuidv4(),
        };
      }),
    [data?.results]
  );

  return (
    <>
      <SharedDataGridServer
        slots={{
          toolbar: CustomToolbar,
          ...createGridOverlays(
            'Logs',
            <History />,
            paginationModel.dateRange[0] !== null ||
              paginationModel.dateRange[1] !== null
              ? 'Try updating your search or filters.'
              : ''
          ),
        }}
        disableColumnMenu
        data={data}
        rows={parsedData || []}
        columns={columns}
        sx={{
          '.MuiDataGrid-cell': {
            whiteSpace: 'normal !important',
            wordWrap: 'break-word !important',
          },
        }}
        initialState={{
          pagination: {
            paginationModel: { pageSize: pageSize, page: 0 },
            rowCount: 0,
          },
        }}
        loading={isLoading || isFetching}
        disableColumnSelector
        disableRowSelectionOnClick
        pageSizeOptions={[pageSize]}
        paginationModel={paginationModel}
        onPaginationModelChange={(newModel) => {
          setPaginationModel({
            ...newModel,
            cursor: data?.cursor || '',
            dateRange: paginationModel.dateRange,
          });
        }}
      />
    </>
  );
}
